import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RefreshCcw, Plus, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const RecurringDepositProductsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', short_name: '', description: '', currency: 'ZAR', min_deposit_amount: '', mandatory_deposit_amount: '', interest_rate: '', compounding_period: 'monthly', interest_posting_period: 'quarterly', min_term: '', max_term: '', pre_closure_penalty: '' });
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [contractFile, setContractFile] = useState(null);
  const contractInputRef = React.useRef(null);
  const { user } = useAuth();

  useEffect(() => { load(); loadPending(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/recurring-deposit-products'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const loadPending = async () => {
    try {
      const res = await api.get('/pending-product-changes');
      setPendingChanges(res.data.filter(c => c.product_type === 'recurring_deposit_product'));
    } catch { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Product name is required'); return; }
    try {
      await api.post('/recurring-deposit-products', {
        ...form,
        min_deposit_amount: parseFloat(form.min_deposit_amount) || 0,
        mandatory_deposit_amount: parseFloat(form.mandatory_deposit_amount) || 0,
        interest_rate: parseFloat(form.interest_rate) || 0,
        min_term: parseInt(form.min_term) || 0, max_term: parseInt(form.max_term) || 0,
        pre_closure_penalty: parseFloat(form.pre_closure_penalty) || 0,
      });
      toast.success('Recurring deposit product created');
      setShowModal(false);
      setForm({ name: '', short_name: '', description: '', currency: 'ZAR', min_deposit_amount: '', mandatory_deposit_amount: '', interest_rate: '', compounding_period: 'monthly', interest_posting_period: 'quarterly', min_term: '', max_term: '', pre_closure_penalty: '' });
      load();
    } catch { toast.error('Failed to create'); }
  };

  const openEditModal = (p) => {
    setEditProduct(p);
    setEditForm({
      name: p.name || '', short_name: p.short_name || '', description: p.description || '',
      min_deposit_amount: p.min_deposit_amount || p.deposit_amount || '',
      mandatory_deposit_amount: p.mandatory_deposit_amount || p.deposit_amount || '',
      interest_rate: p.interest_rate || '',
      compounding_period: p.compounding_period || p.interest_compounding || 'monthly',
      interest_posting_period: p.interest_posting_period || p.interest_posting || 'quarterly',
      min_term: p.min_term || p.min_deposit_term || '', max_term: p.max_term || p.max_deposit_term || '',
      pre_closure_penalty: p.pre_closure_penalty || p.penalty_rate || '',
      effective_date: new Date().toISOString().slice(0, 10),
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        ...editForm,
        min_deposit_amount: parseFloat(editForm.min_deposit_amount) || undefined,
        mandatory_deposit_amount: parseFloat(editForm.mandatory_deposit_amount) || undefined,
        interest_rate: parseFloat(editForm.interest_rate) || undefined,
        min_term: parseInt(editForm.min_term) || undefined, max_term: parseInt(editForm.max_term) || undefined,
        pre_closure_penalty: parseFloat(editForm.pre_closure_penalty) || 0,
      };
      const res = await api.put(`/recurring-deposit-products/${editProduct.id}`, payload);
      if (res.data.status === 'pending') {
        toast.success('Product change submitted for checker approval');
      } else {
        toast.success('Product updated');
      }
      setShowEditModal(false); load(); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleApprove = async (changeId) => {
    try { await api.post(`/pending-product-changes/${changeId}/approve`, { comments: '' }); toast.success('Approved'); loadPending(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleReject = async (changeId) => {
    try { await api.post(`/pending-product-changes/${changeId}/reject`, { comments: '' }); toast.success('Rejected'); loadPending(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleContractUpload = async (productId) => {
    if (!contractFile) return;
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const b64 = e.target.result.split(',')[1];
        await api.post(`/recurring-deposit-products/${productId}/contract/upload`, { file_data: b64, file_name: contractFile.name, file_type: contractFile.type });
        toast.success('Contract uploaded'); setContractFile(null); load();
      };
      reader.readAsDataURL(contractFile);
    } catch { toast.error('Failed to upload contract'); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="rd-products-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Products</span><span>/</span><span className="text-[#1f1f1f] font-medium">Recurring Deposit Products</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">PRODUCT CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Recurring Deposit Products</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-rd-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Create RD Product
        </Button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="rd-products-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Description</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Mandatory Deposit</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Interest Rate</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Term</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400"><RefreshCcw className="w-8 h-8 mx-auto mb-2 text-gray-300" />No recurring deposit products.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`rd-row-${item.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{item.name}</div><div className="text-xs text-gray-400">{item.short_name}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">{item.description || '-'}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">R {(item.mandatory_deposit_amount || item.deposit_amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-[#C5A059]">{item.interest_rate || 0}%</td>
                <td className="px-4 py-3 text-sm text-gray-600">{item.min_term || item.min_deposit_term || 0} - {item.max_term || item.max_deposit_term || 0} months</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${item.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{item.is_active !== false ? 'Active' : 'Inactive'}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-500 hover:text-[#1f1f1f]" onClick={() => openEditModal(item)} data-testid={`rd-edit-${item.id}`}>Edit</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="rd-desc"><DialogHeader><DialogTitle>Create Recurring Deposit Product</DialogTitle></DialogHeader>
          <p id="rd-desc" className="sr-only">Form</p>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div><Label>Product Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="rd-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Short Name</Label><Input value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})} /></div>
              <div><Label>Currency</Label><Input value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Min Deposit (ZAR)</Label><Input type="number" value={form.min_deposit_amount} onChange={e => setForm({...form, min_deposit_amount: e.target.value})} /></div>
              <div><Label>Mandatory Monthly (ZAR)</Label><Input type="number" value={form.mandatory_deposit_amount} onChange={e => setForm({...form, mandatory_deposit_amount: e.target.value})} /></div>
            </div>
            <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} data-testid="rd-rate-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Compounding</Label>
                <Select value={form.compounding_period} onValueChange={v => setForm({...form, compounding_period: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Posting Period</Label>
                <Select value={form.interest_posting_period} onValueChange={v => setForm({...form, interest_posting_period: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="annually">Annually</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Min Term (months)</Label><Input type="number" value={form.min_term} onChange={e => setForm({...form, min_term: e.target.value})} /></div>
              <div><Label>Max Term (months)</Label><Input type="number" value={form.max_term} onChange={e => setForm({...form, max_term: e.target.value})} /></div>
              <div><Label>Penalty (%)</Label><Input type="number" step="0.01" value={form.pre_closure_penalty} onChange={e => setForm({...form, pre_closure_penalty: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="rd-submit-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="rd-edit-desc">
          <DialogHeader><DialogTitle>Edit: {editProduct?.name}</DialogTitle></DialogHeader>
          <p id="rd-edit-desc" className="sr-only">Edit form</p>
          {editProduct && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                {user?.role === 'admin' ? 'Admin: changes apply immediately.' : 'Changes will be submitted for checker approval.'}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                <div><Label>Short Name</Label><Input value={editForm.short_name} onChange={e => setEditForm({...editForm, short_name: e.target.value})} /></div>
              </div>
              <div><Label>Description</Label><Input value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={editForm.interest_rate} onChange={e => setEditForm({...editForm, interest_rate: e.target.value})} /></div>
                <div><Label>Mandatory Deposit</Label><Input type="number" value={editForm.mandatory_deposit_amount} onChange={e => setEditForm({...editForm, mandatory_deposit_amount: e.target.value})} /></div>
                <div><Label>Effective Date</Label><Input type="date" value={editForm.effective_date} onChange={e => setEditForm({...editForm, effective_date: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Min Term</Label><Input type="number" value={editForm.min_term} onChange={e => setEditForm({...editForm, min_term: e.target.value})} /></div>
                <div><Label>Max Term</Label><Input type="number" value={editForm.max_term} onChange={e => setEditForm({...editForm, max_term: e.target.value})} /></div>
                <div><Label>Penalty (%)</Label><Input type="number" step="0.01" value={editForm.pre_closure_penalty} onChange={e => setEditForm({...editForm, pre_closure_penalty: e.target.value})} /></div>
              </div>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <div className="p-3 border border-gray-200 bg-gray-50 space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">T&C / Contract Document</Label>
                  {editProduct.contract_file_name && (
                    <div className="flex items-center gap-2 text-xs text-gray-600"><FileText className="w-3.5 h-3.5" /><span>Current: {editProduct.contract_file_name} (v{editProduct.contract_version})</span></div>
                  )}
                  <div className="flex gap-2 items-center">
                    <Input type="file" accept=".pdf,.doc,.docx" ref={contractInputRef} onChange={e => setContractFile(e.target.files?.[0])} className="text-xs" />
                    <Button size="sm" disabled={!contractFile} onClick={() => handleContractUpload(editProduct.id)} className="text-xs bg-[#1f1f1f] text-white" data-testid="rd-upload-contract-btn">Upload</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button><Button onClick={handleEditSubmit} className="bg-[#1f1f1f] text-white" data-testid="rd-edit-submit">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Changes */}
      {pendingChanges.length > 0 && (user?.role === 'admin' || user?.role === 'manager') && (
        <div className="bg-white border border-amber-200 p-4" data-testid="rd-pending-changes-section">
          <h3 className="text-sm font-bold text-[#1f1f1f] mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Pending RD Product Changes ({pendingChanges.length})</h3>
          <div className="space-y-2">
            {pendingChanges.map(pc => (
              <div key={pc.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100">
                <div>
                  <p className="text-sm font-medium">{pc.product_name}</p>
                  <p className="text-xs text-gray-500">By: {pc.maker_name} ({pc.maker_role})</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApprove(pc.id)} className="bg-green-600 text-white text-xs">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => handleReject(pc.id)} className="text-xs text-red-600 border-red-200">Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default RecurringDepositProductsPage;
